import { type Reducer } from '@reduxjs/toolkit'
import { subDays } from 'date-fns'
import { produce } from 'immer'
import { type ActionType, getType } from 'typesafe-actions'
import { type OrganisationLocationType } from '@/connect-types/organisations/organisations.type'
import { ServiceErrorHandler } from '@/connect-types/backend/service'
import message from '@/common/message/message'
import * as actions from './global.actions'
import { type DefaultQueryType, initialDefaultQuery } from './global.types'
import { type DefaultAnalyticsRequest } from '@/connect-types/analytics/request.type'

export type GlobalAction = ActionType<typeof actions>

/**
 * NOTE: interface MUST be named 'StateType' (corresponding to StateType
 * from typesafe-actions)
 */
export interface StateType {
  readonly infoBanner: {
    readonly isOpen: boolean
  }
  menu: {
    isOpen: boolean
  }
  selectedLocation: OrganisationLocationType | null
  analyticsPayload: DefaultAnalyticsRequest
  filterOpen: boolean
  chartStyle: string
  isCompound: boolean
  locationModalVisible: boolean
  isDemoMode: boolean
  orgId: string
  selectedPartnerOrg: string
  token: string
  selectedReviewPageId: string
  listStyle: 'list' | 'grid'
  query: DefaultQueryType

  pagination: {
    search: string
  }
}

/**
 * Reducer's initial state
 */
const initialState: StateType = {
  query: initialDefaultQuery,
  listStyle: 'grid',
  infoBanner: {
    isOpen: false,
  },
  menu: {
    isOpen: true,
  },
  selectedLocation: null,
  selectedReviewPageId: '',
  analyticsPayload: {
    serial: '',
    end: new Date().getTime(),
    start: subDays(new Date(), 7).getTime(),
  },
  filterOpen: false,
  chartStyle: 'line',
  isCompound: false,
  locationModalVisible: false,
  isDemoMode: false,
  orgId: '',
  selectedPartnerOrg: '',
  token: '',

  pagination: {
    search: '',
  },
}

const globalReducer: Reducer<StateType, GlobalAction> = (
  state = initialState,
  action
) =>
  produce<StateType>(state, (draft: any) => {
    switch (action.type) {
      case getType(actions.setInitialSearch): {
        draft.pagination.search = action.payload.term
        break
      }
      case getType(actions.setQuery): {
        draft.query = { ...draft.query, ...action.payload.query }
        break
      }

      case getType(actions.setOrgId): {
        if (draft.orgId !== action.payload.orgId) {
          draft.orgId = action.payload.orgId
          draft.selectedLocation = null
        }

        break
      }

      /**
       * GLOBAL_SET_DEMO_MODE
       *
       * Set the demo mode
       */
      case getType(actions.setDemoMode): {
        draft.isDemoMode = action.payload.isDemoMode
        break
      }

      case getType(actions.setAnalyticsPayload): {
        draft.analyticsPayload = action.payload.request
        break
      }

      case getType(actions.setSelectedReviewPageId): {
        draft.selectedReviewPageId = action.payload.id
        break
      }
      case getType(actions.setListStyle): {
        draft.listStyle = action.payload
        break
      }
    }
  })

export default globalReducer
